import { useState, useMemo } from 'react';
import useScrollReveal from '../useScrollReveal';
import useGsapReveal from '../useGsapReveal';
import { sectionStyles, getAnimation, resolveImage, hexToRgba } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionBgImage from './SectionBgImage';

export default function FaqSection({ block, primaryColor, secondaryColor, enableAnimations, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const rawItems = content.items || [];
    const items = rawItems.length > 0 ? rawItems : [
        { question: 'Como puedo empezar?', answer: 'Registrate gratis y tendras acceso inmediato a todas las funcionalidades basicas.' },
        { question: 'Ofrecen periodo de prueba?', answer: 'Si, ofrecemos 14 dias de prueba gratuita con acceso completo.' },
        { question: 'Puedo cancelar en cualquier momento?', answer: 'Si, puedes cancelar tu suscripcion en cualquier momento sin penalidades.' },
    ];
    const [openIndex, setOpenIndex] = useState(null);
    const [activeCategory, setActiveCategory] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const anim = getAnimation(config, visible);
    const variant = config.variant || 'accordion';
    const gsapRef = useGsapReveal({ animation: 'stagger-children', staggerDelay: 0.06, disabled: !enableAnimations });
    const faqColumns = config.columns || 3;
    const faqColClass = faqColumns === 2 ? 'md:grid-cols-2' : faqColumns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';

    const sectionCls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const itemBgStyle = { backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#ffffff' };
    const itemBorderStyle = s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';
    const itemBorderOpenStyle = s.dark ? 'rgba(255,255,255,0.2)' : '#d1d5db';

    // ── Search filter ──
    const filteredBySearch = useMemo(() => {
        if (!searchQuery.trim()) return items;
        const q = searchQuery.toLowerCase();
        return items.filter(item =>
            (item.question || '').toLowerCase().includes(q) ||
            (item.answer || '').toLowerCase().includes(q)
        );
    }, [items, searchQuery]);

    // ── Categories (for categorized variant) ──
    const categories = useMemo(() => {
        const cats = [];
        items.forEach(item => {
            if (item.category && !cats.includes(item.category)) cats.push(item.category);
        });
        return cats;
    }, [items]);

    const filteredByCategory = useMemo(() => {
        let result = filteredBySearch;
        if (activeCategory) {
            result = result.filter(item => item.category === activeCategory);
        }
        return result;
    }, [filteredBySearch, activeCategory]);

    if (items.length === 0) return null;

    // ── Search input component ──
    const SearchInput = () => {
        if (!config.show_search) return null;
        return (
            <div className="relative mb-6 max-w-md mx-auto">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <svg className="w-4 h-4" style={{ color: s.textSub }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
                    </svg>
                </div>
                <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder={content.search_placeholder || 'Buscar pregunta...'}
                    className={`w-full pl-10 pr-10 py-2.5 rounded-xl border text-sm transition-colors ${s.dark ? 'placeholder-white/40' : 'placeholder-gray-400'}`}
                    style={{
                        backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#ffffff',
                        borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#d1d5db',
                        color: s.text,
                        outlineColor: s.accent,
                    }}
                />
                {searchQuery && (
                    <button onClick={() => setSearchQuery('')} className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <svg className="w-4 h-4" style={{ color: s.textSub }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>
                )}
            </div>
        );
    };

    // ── No results message ──
    const NoResults = () => (
        <div className="text-center py-8">
            <p className="text-sm" style={{ color: s.textSub }}>{content.empty_text || 'No se encontraron resultados'}</p>
        </div>
    );

    const AccordionItem = ({item, i}) => {
        const isOpen = openIndex === i;
        return (
            <div className="rounded-xl border transition-all duration-300"
                style={{ ...itemBgStyle, borderColor: isOpen ? itemBorderOpenStyle : itemBorderStyle }}>
                <button onClick={() => setOpenIndex(isOpen ? null : i)} className="w-full flex items-center justify-between px-6 py-4 text-left">
                    <span className="text-sm font-semibold" style={{ color: s.text }}>{item.question}</span>
                    <svg className={`w-5 h-5 shrink-0 ml-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                        style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                    </svg>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="px-6 pb-4 text-sm leading-relaxed whitespace-pre-line" style={{ color: s.textSub }}>{item.answer}</div>
                </div>
            </div>
        );
    };

    // ── Two-column-grid variant (all answers visible, no accordion) ──
    if (variant === 'two-column-grid') {
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'FAQ'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        {items.map((item, i) => (
                            <div key={i}
                                className={`rounded-xl border p-6 transition-all duration-500 hover:shadow-md hover:-translate-y-0.5 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{
                                    ...itemBgStyle,
                                    borderColor: itemBorderStyle,
                                    transitionDelay: visible ? `${i * 80}ms` : '0ms',
                                }}>
                                <h4 className="font-bold text-sm mb-2" style={{ color: s.text }}>{item.question}</h4>
                                <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: s.textSub }}>{item.answer}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // ── Cards variant ──
    if (variant === 'cards') {
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'FAQ'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className={`grid grid-cols-1 ${faqColClass} gap-6`}>
                        {items.map((item, i) => (
                            <div key={i} className={`rounded-xl border p-6 transition-all duration-500 hover:-translate-y-1 hover:shadow-md ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ ...itemBgStyle, borderColor: itemBorderStyle, transitionDelay: visible ? `${i * 80}ms` : '0ms' }}>
                                <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: hexToRgba(s.accent, 0.08) }}>
                                    <span className="text-sm font-bold" style={{ color: s.accent }}>?</span>
                                </div>
                                <h4 className="font-semibold text-sm mb-2" style={{ color: s.text }}>{item.question}</h4>
                                <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.answer}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── With-image variant ──
    if (variant === 'with-image') {
        const imgUrl = resolveImage(content, 'image', mediaFolders);
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'FAQ'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
                        <div className="rounded-2xl overflow-hidden lg:sticky lg:top-24">
                            {imgUrl ? (
                                <img src={imgUrl} alt={content.title || ''} className="w-full aspect-[4/3] object-cover rounded-2xl" loading="lazy"/>
                            ) : (
                                <div className="w-full aspect-[4/3] rounded-2xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${hexToRgba(s.accent, 0.19)}, ${hexToRgba(s.accent, 0.06)})` }}>
                                    <span className="text-6xl opacity-30">?</span>
                                </div>
                            )}
                        </div>
                        <div className="space-y-3">
                            {items.map((item, i) => <AccordionItem key={i} item={item} i={i}/>)}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Columns variant ──
    if (variant === 'columns') {
        const half = Math.ceil(items.length / 2);
        const left = items.slice(0, half);
        const right = items.slice(half);
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'FAQ'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">{left.map((item, i) => <AccordionItem key={i} item={item} i={i}/>)}</div>
                        <div className="space-y-3">{right.map((item, i) => <AccordionItem key={half + i} item={item} i={half + i}/>)}</div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Timeline variant ──
    if (variant === 'timeline') {
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'FAQ'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="relative pl-8">
                        <div className="absolute left-3 top-0 bottom-0 w-px" style={{ backgroundColor: hexToRgba(s.accent, 0.19) }} />
                        <div className="space-y-6">
                            {items.map((item, i) => (
                                <div key={i} className={`relative transition-all duration-500 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
                                    style={{ transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                    <div className="absolute -left-[22px] w-5 h-5 rounded-full border-2 flex items-center justify-center"
                                        style={{ borderColor: s.accent, backgroundColor: s.dark ? '#1f2937' : '#ffffff' }}>
                                        <span className="text-[8px] font-bold" style={{ color: s.accent }}>?</span>
                                    </div>
                                    <h4 className="font-semibold text-sm mb-1" style={{ color: s.text }}>{item.question}</h4>
                                    <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>{item.answer}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Categorized variant ──
    if (variant === 'categorized') {
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'FAQ'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <SearchInput />
                    {categories.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-6 justify-center">
                            <button
                                onClick={() => setActiveCategory(null)}
                                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200`}
                                style={!activeCategory
                                    ? { backgroundColor: s.accent, color: '#fff' }
                                    : { backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', color: s.textSub }
                                }
                            >
                                {content.all_label || 'Todas'}
                            </button>
                            {categories.map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
                                    className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200`}
                                    style={activeCategory === cat
                                        ? { backgroundColor: s.accent, color: '#fff' }
                                        : { backgroundColor: s.dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)', color: s.textSub }
                                    }
                                >
                                    {cat}
                                </button>
                            ))}
                        </div>
                    )}
                    {filteredByCategory.length === 0 ? <NoResults /> : (
                        <div className="space-y-3">
                            {filteredByCategory.map((item, i) => <AccordionItem key={i} item={item} i={i}/>)}
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Chat variant ──
    if (variant === 'chat') {
        return (
            <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'FAQ'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <SearchInput />
                    {filteredBySearch.length === 0 ? <NoResults /> : (
                        <div className="space-y-4">
                            {filteredBySearch.map((item, i) => {
                                const isOpen = openIndex === i;
                                return (
                                    <div key={i} className={`transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                        style={{ transitionDelay: visible ? `${i * 80}ms` : '0ms' }}>
                                        {/* Question bubble - right aligned */}
                                        <div className="flex justify-end mb-2">
                                            <button onClick={() => setOpenIndex(isOpen ? null : i)}
                                                className="max-w-[85%] text-left px-4 py-3 rounded-2xl rounded-br-sm text-sm font-medium transition-all duration-200 hover:shadow-md"
                                                style={{ backgroundColor: s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6', color: s.text }}>
                                                {item.question}
                                                <svg className={`inline-block w-3.5 h-3.5 ml-2 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                                                    style={{ color: s.textSub }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7"/>
                                                </svg>
                                            </button>
                                        </div>
                                        {/* Answer bubble - left aligned */}
                                        <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                                            <div className="flex justify-start">
                                                <div className="max-w-[85%] px-4 py-3 rounded-2xl rounded-bl-sm text-sm leading-relaxed whitespace-pre-line"
                                                    style={{ backgroundColor: hexToRgba(s.accent, 0.12), color: s.text }}>
                                                    {item.answer}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // ── Accordion (default) ──
    const accordionItems = filteredBySearch;
    return (
        <section className={sectionCls} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
            <div ref={ref} className={`relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag || 'FAQ'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                <SearchInput />
                {accordionItems.length === 0 ? <NoResults /> : (
                    <div ref={gsapRef} className="space-y-3">
                        {accordionItems.map((item, i) => <AccordionItem key={i} item={item} i={i}/>)}
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
